import { GridPaginationModel } from '@mui/x-data-grid';
import { useContext, useState } from 'react';
import api from '../api/api';
import constants, { SORT } from '../constants/constants';
import { Collection } from '../models/collection-types';
import { Direction } from '../models/collectionQuery.model';
import { getPollingCtrl } from './pollingController';
import { i18n } from '../i18n/i18n';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { SearchReason, obj } from '../models/common-types';
import { NOTIFICATION, useNotifications } from '@cellebrite/saas-ui-common';
import { isSuccess } from '../utils/communication';
import { PENDO_EVENTS, pendoTrack } from '../utils/pendo';
import { AuthContext } from './authContext';

export interface Dictionary {
    first_name: string;
    last_name: string;
    middle_name?: string;
    username?: string;
    city?: string;
    state?: string;
    country?: string;
    age?: string;
    start_date?: string;
    end_date?: string;
    work?: string;
    past_work?: string;
    industry?: string;
    education?: string;
    phone?: string;
    email?: string;
    vin?: string;
    crf?: string;
    npi?: string;
}

export interface CreateCollectionDto {
    hideMessage?: boolean;
    searchTerm: string;
    searchType: string;
    analysisType: string;
    displayName?: string;
    searchReason?: {
        reason?: string;
        comment?: string;
        description?: string;
    };
    advancedSearchInputs?: Dictionary;
}

export interface MoveCollectionsDto {
    targetCase: string;
    collections: string[];
}

export interface UpdateCollectionDto {
    name?: string;
    favorite?: boolean;
    note?: obj<any>;
}

export default function useCollections(caseId: string | null, wsId: number | null, streamId: string) {
    const {
        preferences: { searchReasons },
    } = useContext(AuthContext);
    const [showMessage] = useNotifications();
    const [direction, setDirection] = useState(SORT.DIRECTION.DESC as unknown as Direction);
    const [find, setFind] = useState('');
    const [types, setTypes] = useState([]);
    const [inputTypeFilters, setInputTypeFilters] = useState([]);
    const [watchListsFilters, setWatchListsFilters] = useState([]);
    const [selectedReason, setSelectedReason] = useState<SearchReason>(null);
    const pollingCtrl = getPollingCtrl();
    const queryClient = useQueryClient();
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });

    const {
        data: collQuery,
        isLoading,
        isPreviousData,
    } = useQuery(
        ['collections', wsId, caseId, paginationModel, direction, types, inputTypeFilters, watchListsFilters, find],
        () =>
            api.collections.fetchCollections(
                wsId,
                caseId,
                { offset: paginationModel.page, limit: paginationModel.pageSize },
                { order: direction, property: 'createdAt' },
                types,
                inputTypeFilters,
                watchListsFilters,
                find,
            ),
        {
            enabled: !!wsId && !!caseId,
            refetchInterval: !pollingCtrl.stopCollectionPollingInfo ? constants.COLLECTIONS_POLLING_DELAY : false,
            keepPreviousData: true,
        },
    );

    const getStoredOrDefaultReason = (reasons: SearchReason[], label: string) =>
        label ? reasons.find((reason) => reason.label === label) : reasons[0];

    const createCollection = useMutation(
        (createObj: { pendoEventName: string; newSearch: CreateCollectionDto }) => {
            pendoTrack(createObj.pendoEventName, {
                searchTerm: createObj.newSearch.searchTerm,
                searchType: createObj.newSearch.searchType,
                analysisType: createObj.newSearch.analysisType,
                wsId,
                caseId,
            });

            pendoTrack(PENDO_EVENTS.SEARCH_ALL, {});
            const storedLocaition = localStorage.getItem('searchReason');
            return api.collections.createCollection(wsId, caseId, {
                ...createObj.newSearch,
                searchReason: selectedReason || getStoredOrDefaultReason(searchReasons, storedLocaition),
            });
        },
        {
            onSuccess: (res, createObj) => {
                const storedLocaition = localStorage.getItem('searchReason');
                setSelectedReason(getStoredOrDefaultReason(searchReasons, storedLocaition));
                if (res.data.error && res.data.error.includes('Fail to require quota')) {
                    showMessage(res.data.error, NOTIFICATION.TYPE.WARNING);
                } else {
                    showMessage(
                        i18n.translate('NEW_COLLECTION_SUCCESS', {
                            searchTerm: createObj.newSearch.displayName || createObj.newSearch.searchTerm,
                        }),
                        NOTIFICATION.TYPE.SUCCESS,
                    );
                }
                queryClient.invalidateQueries('collections');
                queryClient.invalidateQueries('quotaValid');
            },
            onError: (_err, createObj) =>
                showMessage(
                    i18n.translate('NEW_COLLECTION_ERROR', {
                        searchTerm: createObj.newSearch.displayName || createObj.newSearch.searchTerm,
                    }),
                    NOTIFICATION.TYPE.ERROR,
                ),
        },
    );

    const deleteCollections = useMutation(
        (collections: Collection[]) =>
            api.collections.deleteCollections(
                wsId,
                caseId,
                collections.map(({ id }) => id),
            ),
        {
            onSuccess: (_res, collections) =>
                collections.forEach((col, index) =>
                    showMessage(
                        i18n.translate(
                            isSuccess(_res[index]) ? 'DELETE_COLLECTION_SUCCESS' : 'DELETE_COLLECTION_ERROR',
                            {
                                name: col.displayName,
                            },
                        ),
                        isSuccess(_res[index]) ? NOTIFICATION.TYPE.SUCCESS : NOTIFICATION.TYPE.ERROR,
                    ),
                ),
            onError: (_err, collections) =>
                collections.forEach((col) =>
                    showMessage(
                        i18n.translate('DELETE_COLLECTION_ERROR', { name: col.displayName }),
                        NOTIFICATION.TYPE.ERROR,
                    ),
                ),
            onSettled: () => queryClient.invalidateQueries('collections'),
        },
    );

    const rerunCollections = useMutation(
        (rerunObj: { pendoEventName: string; collections: Collection[] }) => {
            const collectionIds = rerunObj.collections.map(({ id }) => id);
            collectionIds.forEach((id) => {
                pendoTrack(rerunObj.pendoEventName, { collectionId: id, wsId, caseId });
            });
            return api.collections.duplicateCollection(wsId, caseId, collectionIds);
        },
        {
            onSuccess: (_res, rerunObj) => {
                queryClient.invalidateQueries('quotaValid');
                return rerunObj.collections.forEach((col, index) =>
                    showMessage(
                        i18n.translate(isSuccess(_res[index]) ? 'RERUN_COLLECTION_SUCCESS' : 'RERUN_COLLECTION_ERROR', {
                            name: col.displayName,
                        }),
                        isSuccess(_res[index]) ? NOTIFICATION.TYPE.SUCCESS : NOTIFICATION.TYPE.ERROR,
                    ),
                );
            },
            onError: (_err, rerunObj) =>
                rerunObj.collections.forEach((col) =>
                    showMessage(
                        i18n.translate('RERUN_COLLECTION_ERROR', { name: col.displayName }),
                        NOTIFICATION.TYPE.ERROR,
                    ),
                ),
            onSettled: () => queryClient.invalidateQueries('collections'),
        },
    );

    // @ts-ignore
    const updateCollection = useMutation(
        (params: { payload: UpdateCollectionDto; colId; successCallback?: () => void; errorCallback?: () => void }) =>
            api.collections.updateCollection(wsId, caseId, params.colId, params.payload),
        {
            onSuccess: (data, params) => {
                params.successCallback?.();
                return data;
            },
            onError: (data, params) => {
                params.errorCallback?.();
                return data;
            },
            onSettled: () => queryClient.invalidateQueries('collections'),
        },
    );

    const moveCollectionToCase = useMutation(
        (params: { pendoEventName: string; colIds: string[]; destinationCase: string }) => {
            const collections = params.colIds.map((id) => id.toString());
            pendoTrack(params.pendoEventName, {
                collections: collections,
                targetCase: params.destinationCase,
            });
            return api.collections.moveCollectionToCase(wsId, caseId, {
                targetCase: params.destinationCase,
                collections: collections,
            });
        },
        { onSuccess: () => queryClient.invalidateQueries('collections') },
    );

    const addToReport = useMutation(
        (addObj: { pendoEventName: string; payload: any }) => {
            pendoTrack(addObj.pendoEventName, {
                searchTerm: addObj.payload.searchTerm,
                searchType: addObj.payload.searchType,
                searchDisplayName: addObj.payload.displaySearchTerm,
                streamId: addObj.payload.streamId,
                wsId,
                caseId,
            });
            return api.collections.addToReport(wsId, caseId, addObj.payload.streamId || streamId, addObj.payload);
        },
        {
            onSuccess: (_res, addObj) => {
                showMessage(
                    i18n.translate('ADD_TO_REPORT_SUCCESS', { searchTerm: addObj.payload.searchTerm }),
                    NOTIFICATION.TYPE.SUCCESS,
                );
                queryClient.invalidateQueries('quotaValid');
            },
            onError: (_err, addObj) =>
                showMessage(
                    i18n.translate('ADD_TO_REPORT_ERROR', { searchTerm: addObj.payload.searchTerm }),
                    NOTIFICATION.TYPE.SUCCESS,
                ),
        },
    );

    return {
        inputTypeFilters,
        watchListsFilters,
        collections: collQuery?.collections || [],
        rowsCount: collQuery?.rowsCount || 0,
        isLoading,
        direction,
        types,
        find,
        paginationModel,
        setPaginationModel,
        setFind,
        setTypes,
        setDirection,
        setSelectedReason,
        setInputTypeFilters,
        setWatchListsFilters,
        moveCollectionToCase,
        createCollection,
        updateCollection: updateCollection.mutate,
        addToReport: addToReport.mutate,
        rerunCollections: rerunCollections.mutate,
        deleteCollections: deleteCollections.mutate,
        // using it to smoothly update collections data grid
        isPreviousData,
    };
}
